import { TicketType } from 'features/common/types';
import { SeatsIOCategory } from 'services/Seats';
import { CHART_SERIES_COLORS } from './chartColors';

/**
 * Neutral grey for a ticket type that is NOT in the event's current type list — e.g. a deleted type
 * still referenced on an old order/comp row. Reads as "uncategorised", not a real brand/status colour.
 *
 * Must be a hex literal: `PunchupTag`'s `color-mix()` needs a concrete value (a `var()` token does not
 * resolve as a `color-mix()` argument). This is the value of `--punchup-color-text-secondary` — keep the
 * two in sync if that token ever changes. `PunchupTicketTypeTag.TICKET_TYPE_TAG_FALLBACK` re-exports this.
 */
export const TICKET_TYPE_FALLBACK_COLOR = '#787B7E';

/** The minimum a ticket type must carry to be colour-resolved (the full `TicketType` satisfies it). */
export type ColorableTicketType = Pick<TicketType, 'ID' | 'name' | 'color'>;

/** The minimum a ticket type must carry to be matched to its bound seats.io category colour. */
export type SeatsBoundTicketType = Pick<TicketType, 'name' | 'seatsCategoryKey'>;

/**
 * SEATED events only: maps each ticket type's NAME → its bound seats.io category colour, so the dashboard
 * display (tags, chips, rails, charts) mirrors the seats chart. Includes only tiers whose `seatsCategoryKey`
 * matches a category that carries a colour. Returns `{}` when `seatsCategories` is empty/undefined — which
 * is exactly the non-seated case AND the loading/error/unreachable seats-fetch case, so the caller then
 * falls through to the tier's stored colour → palette. Pure; the seats knowledge lives only here.
 */
export function buildSeatsCategoryColorMap(
	ticketTypes: SeatsBoundTicketType[],
	seatsCategories: SeatsIOCategory[] | undefined
): Record<string, string> {
	if (!seatsCategories?.length) return {};
	const colorByKey = new Map(seatsCategories.filter((c) => c.color).map((c) => [String(c.key), c.color] as const));
	const byName: Record<string, string> = {};
	for (const tt of ticketTypes) {
		const key = tt.seatsCategoryKey ? String(tt.seatsCategoryKey) : '';
		const color = key ? colorByKey.get(key) : undefined;
		if (color) byName[tt.name] = color;
	}
	return byName;
}

/**
 * Builds the canonical ticket-type-NAME → display-colour map for an event. The single source of truth
 * shared by the analytics charts, the table tags, the pool member chips, and the ticket-type tile rails,
 * so a tier reads the SAME colour everywhere it appears.
 *
 * A type's own stored colour (`TicketType.color`) wins when set and not already claimed by another type;
 * types without a usable/unique colour fall back to `CHART_SERIES_COLORS`, assigned in a STABLE order
 * (sorted by id) so a type's fallback colour does not shift between pages or across renders. The
 * "no two types share a colour" invariant holds (the analytics charts rely on it).
 *
 * Keyed by NAME — the only join key stable across the event API and the public ticket-types endpoint
 * (their numeric ids differ; see `useEventCapacity` / SendTickets `poolBudget`). Name collisions (two
 * types with the same name in one event — rare) intentionally share a colour.
 */
export function buildTicketTypeColorMap(
	ticketTypes: ColorableTicketType[],
	priorityOverride?: Record<string, string>
): Record<string, string> {
	// Assign fallback palette slots by ascending id so a type keeps its colour regardless of the array
	// order a given page received (and regardless of a later drag-reorder).
	const ordered = [...ticketTypes].sort((a, b) => a.ID - b.ID);
	const byName: Record<string, string> = {};
	const used = new Set<string>();
	const fallback: ColorableTicketType[] = [];

	// A priority override (e.g. a seated tier's bound seats.io category colour) wins over the stored
	// colour and the palette, and claims its colour in `used` so no other tier collides with it. Two
	// tiers sharing one override colour (same seats category) intentionally share it.
	for (const tt of ordered) {
		const override = priorityOverride?.[tt.name];
		if (override) {
			byName[tt.name] = override;
			used.add(override.toUpperCase());
		}
	}

	for (const tt of ordered) {
		if (byName[tt.name]) continue; // already set by a priority override
		const stored = tt.color ? tt.color.toUpperCase() : '';
		if (stored && !used.has(stored)) {
			byName[tt.name] = stored;
			used.add(stored);
		} else {
			fallback.push(tt);
		}
	}

	let idx = 0;
	for (const tt of fallback) {
		// Advance past palette colours already claimed by a stored colour; the guard lets it wrap rather
		// than loop forever if every palette entry is taken.
		let color = CHART_SERIES_COLORS[idx % CHART_SERIES_COLORS.length];
		let guard = 0;
		while (used.has(color.toUpperCase()) && guard < CHART_SERIES_COLORS.length) {
			idx += 1;
			color = CHART_SERIES_COLORS[idx % CHART_SERIES_COLORS.length];
			guard += 1;
		}
		idx += 1;
		used.add(color.toUpperCase());
		byName[tt.name] = color;
	}

	return byName;
}
